import { Container, TrackingData, TrackingEvent } from '../types';
import { format, addDays } from 'date-fns';

interface ApiContainerEvent {
  id: string;
  eventType: string;
  eventDate: string;
  location: string;
  title: string;
  vesselName?: string;
  voyage?: string;
  isEstimated?: boolean;
}

interface ContainerWithEvents extends Container {
  events?: ApiContainerEvent[];
}

interface ShipmentTrackingInfo {
  containers: ContainerWithEvents[];
  eta?: string | null;
  destinationPort?: string | null;
}

const ESTIMATED_EVENT_TITLES = {
  ARRIVE: 'Vessel arrival at port of discharge',
  UNLOAD: 'Unloaded from vessel at port of discharging',
  GATEOUT: 'Gate out from inbound terminal',
  DELIVER: 'Delivery to consignee',
} as const;

function generateEstimatedEvents(
  eta: string,
  destinationPort: string | null
): ApiContainerEvent[] {
  const etaDate = new Date(eta);
  const location = destinationPort || 'Destination';
  
  const estimatedEvents: ApiContainerEvent[] = [
    {
      id: 'est-arrive',
      eventType: 'ARRIVE',
      eventDate: eta,
      location,
      title: ESTIMATED_EVENT_TITLES.ARRIVE,
      isEstimated: true,
    },
    {
      id: 'est-unload',
      eventType: 'UNLOAD',
      eventDate: addDays(etaDate, 1).toISOString(),
      location,
      title: ESTIMATED_EVENT_TITLES.UNLOAD,
      isEstimated: true,
    },
    {
      id: 'est-gateout',
      eventType: 'GATEOUT',
      eventDate: addDays(etaDate, 2).toISOString(),
      location,
      title: ESTIMATED_EVENT_TITLES.GATEOUT,
      isEstimated: true,
    },
    {
      id: 'est-deliver',
      eventType: 'DELIVER',
      eventDate: addDays(etaDate, 3).toISOString(),
      location,
      title: ESTIMATED_EVENT_TITLES.DELIVER,
      isEstimated: true,
    },
  ];

  return estimatedEvents;
}

export function transformContainerEventsToTrackingData(
  containers: ContainerWithEvents[],
  shipmentInfo?: { eta?: string | null; destinationPort?: string | null }
): TrackingData | null {
  const allApiEvents: ApiContainerEvent[] = [];

  containers.forEach(container => {
    if (container.events && Array.isArray(container.events)) {
      allApiEvents.push(...container.events);
    }
  });

  if (shipmentInfo?.eta) {
    const hasArrivalEvent = allApiEvents.some(e => 
      e.eventType === 'ARRIVE' || e.eventType === 'UNLOAD' || e.eventType === 'GATEOUT' || e.eventType === 'DELIVER'
    );
    
    if (!hasArrivalEvent) {
      const estimatedEvents = generateEstimatedEvents(
        shipmentInfo.eta,
        shipmentInfo.destinationPort || null
      );
      allApiEvents.push(...estimatedEvents);
    }
  }

  if (allApiEvents.length === 0) {
    return null;
  }

  const sortedApiEvents = allApiEvents.sort((a, b) => {
    const dateA = new Date(a.eventDate).getTime();
    const dateB = new Date(b.eventDate).getTime();
    return dateA - dateB;
  });

  const hasDeliveryEvent = sortedApiEvents.some(
    event => event.eventType === 'DELIVER'
  );
  const status = hasDeliveryEvent ? 'delivered' : 'in-transit';

  const now = new Date();
  const transformedEvents: TrackingEvent[] = sortedApiEvents.map((event, index) => {
    const eventDate = new Date(event.eventDate);
    const isPast = eventDate <= now;
    const isEstimated = event.isEstimated || !isPast;
    
    return {
      id: event.id || `event-${index}`,
      date: format(eventDate, 'MMM d').toUpperCase(),
      time: format(eventDate, 'h:mm a').toUpperCase(),
      title: event.title,
      location: event.location || 'Unknown',
      status: isPast && !event.isEstimated ? 'completed' : 'estimated',
      isEstimated,
    };
  });

  return {
    status,
    events: transformedEvents,
  };
}
